'use client';

import MenuLogo from '@/components/atoms/MenuLogo';
import type { Navigation } from '@/components/types/Navigation';
import type { AlternativeLanguage } from '@/types/AlternativeLanguage';
import { cn } from '@/utils/cn';
import { X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import LanguageSwitcher from '../LanguageSwitcher';

interface MenuOverlayProps {
    navigation: Navigation;
    locale: string;
    alternativeLanguages: AlternativeLanguage[];
}

export default function MenuOverlay({
    navigation,
    locale,
    alternativeLanguages,
}: MenuOverlayProps) {
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const currentRootPath = pathname.split('/')[1];

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        window.history.pushState({ menu: true }, '');
        document.body.classList.add('modal-active');
    }, []);

    const handlePopState = useCallback((event: PopStateEvent) => {
        if (event.state?.menu === false) {
            setIsOpen(false);
            document.body.classList.remove('modal-active');
        }
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        window.history.pushState({ menu: false }, '');
        document.body.classList.remove('modal-active');
    }, []);

    const handleEscKey = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        },
        [isOpen, handleClose]
    );

    useEffect(() => {
        window.addEventListener('openMenuOverlay', handleOpen);
        window.addEventListener('popstate', handlePopState);
        window.addEventListener('keydown', handleEscKey);

        return () => {
            window.removeEventListener('openMenuOverlay', handleOpen);
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [handleOpen, handlePopState, handleEscKey]);

    if (!isOpen) return null;

    return (
        <nav
            className={cn(
                'overlay-menu active',
                'fixed z-50 h-screen w-screen bg-haug-coral-100 pb-24 font-neue-haas text-haug-ivory-100',
                'overflow-y-auto overflow-x-hidden md:overflow-hidden'
            )}
            aria-modal="true"
            aria-label="Main navigation menu"
        >
            <button
                type="button"
                className={
                    'overlay-menu-close-button sm:-right-2 absolute top-2 right-0 z-50 rounded px-4 py-4 focus:outline-none focus:ring-2 focus:ring-haug-ivory-100 sm:top-0 lg:top-0 lg:right-6'
                }
                onClick={handleClose}
                aria-label={'Close menu'}
            >
                <X size={24} />
            </button>

            <div className={'pt-16 md:flex md:pt-0'}>
                <div
                    className={cn(
                        'w-full md:w-40 lg:w-60',
                        'h-20 md:h-auto',
                        'relative mb-12 md:block'
                    )}
                >
                    <div
                        className={'absolute bottom-0'}
                        style={{ left: '50%', transform: 'translateX(-50%)' }}
                    >
                        <MenuLogo
                            className={'mx-auto mb-4 w-24 px-2 text-haug-ivory-100 md:w-36 lg:w-48'}
                        />
                        <LanguageSwitcher
                            locale={locale}
                            fullLabel={true}
                            alternativeLanguages={alternativeLanguages}
                        />
                    </div>
                </div>
                <div className={'flex flex-grow flex-wrap justify-between md:pt-24'}>
                    <div className={cn('w-full md:w-auto')}>
                        <ul
                            className={cn(
                                'w-full md:w-auto',
                                'md:mt-0',
                                'font-bold text-xl xl:text-3xl',
                                'space-y-4 md:space-y-6 lg:space-y-8',
                                'px-8 pb-4 md:pb-8',
                                'text-center md:text-left'
                            )}
                            role="menu"
                        >
                            {navigation.primary?.map((item) => (
                                <li
                                    key={item.url}
                                    className={cn(
                                        'nav-item',
                                        'border-b pb-3 md:border-b-0',
                                        currentRootPath === item.url ? 'active' : ''
                                    )}
                                >
                                    <Link
                                        href={item.url}
                                        className="block rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-haug-ivory-100"
                                        onClick={handleClose}
                                    >
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={cn('w-full md:w-auto')}>
                        <ul
                            className={cn(
                                'w-full md:w-auto',
                                'md:mt-0',
                                'mb-4',
                                'font-bold text-xl xl:text-3xl',
                                'space-y-4 md:space-y-6 lg:space-y-8',
                                'px-8',
                                'text-center md:text-left'
                            )}
                            role="menu"
                        >
                            {navigation.secondary?.map((item) => (
                                <li
                                    key={item.url}
                                    className={cn(
                                        'nav-item',
                                        'border-b pb-3 md:border-b-0',
                                        currentRootPath === item.url ? 'active' : ''
                                    )}
                                >
                                    <Link
                                        href={item.url}
                                        className="block rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-haug-ivory-100"
                                        onClick={handleClose}
                                    >
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className={'relative hidden w-1/12 md:block'} />
            </div>
        </nav>
    );
}
